<template>
  <div class="row wolt-drive-integration-form">
    <div class="col-md-6">
      <div class="form-group" :class="{ 'has-error': showFieldError('location_id') }">
        <label>{{ fieldLabel('location_id') }}<span v-if="isRequired('location_id')" class="text-danger"> *</span></label>
        <select
          v-model="formData.location_id"
          class="form-control"
          :disabled="disabled"
        >
          <option :value="null">{{ $t('general.select') }}</option>
          <option
            v-for="loc in locations"
            :key="'wolt-drive-loc-' + String(loc._integrationLocationId)"
            :value="loc._integrationLocationId"
            :disabled="isLocationOccupied(loc._integrationLocationId)"
            :class="{ 'integration-location-option--occupied': isLocationOccupied(loc._integrationLocationId) }"
          >
            {{ locationOptionLabel(loc) }}
          </option>
        </select>
        <span v-if="showFieldError('location_id')" class="text-danger help-block">{{ fieldErrorMessage('location_id') }}</span>
      </div>
    </div>

    <div class="col-md-6">
      <div class="form-group" :class="{ 'has-error': showFieldError('api_url') }">
        <label>{{ fieldLabel('api_url') }}<span v-if="isRequired('api_url')" class="text-danger"> *</span></label>
        <input
          type="text"
          class="form-control"
          v-model="formData.api_url"
          :disabled="disabled"
        >
        <span v-if="showFieldError('api_url')" class="text-danger help-block">{{ fieldErrorMessage('api_url') }}</span>
      </div>
    </div>

    <div class="col-md-6">
      <div class="form-group" :class="{ 'has-error': showFieldError('access_token') }">
        <label>{{ fieldLabel('access_token') }}<span v-if="isRequired('access_token')" class="text-danger"> *</span></label>
        <input
          type="text"
          class="form-control"
          v-model="formData.access_token"
          :disabled="disabled"
        >
        <span v-if="showFieldError('access_token')" class="text-danger help-block">{{ fieldErrorMessage('access_token') }}</span>
      </div>
    </div>

    <div class="col-md-6">
      <div class="form-group" :class="{ 'has-error': showFieldError('merchant_id') }">
        <label>{{ fieldLabel('merchant_id') }}<span v-if="isRequired('merchant_id')" class="text-danger"> *</span></label>
        <input
          type="text"
          class="form-control"
          v-model="formData.merchant_id"
          :disabled="disabled"
        >
        <span v-if="showFieldError('merchant_id')" class="text-danger help-block">{{ fieldErrorMessage('merchant_id') }}</span>
      </div>
    </div>

    <div class="col-md-6">
      <div class="form-group" :class="{ 'has-error': showFieldError('venue_id') }">
        <label>{{ fieldLabel('venue_id') }}<span v-if="isRequired('venue_id')" class="text-danger"> *</span></label>
        <input
          type="text"
          class="form-control"
          v-model="formData.venue_id"
          :disabled="disabled"
        >
        <span v-if="showFieldError('venue_id')" class="text-danger help-block">{{ fieldErrorMessage('venue_id') }}</span>
      </div>
    </div>

    <div class="col-md-6">
      <div class="form-group" :class="{ 'has-error': showFieldError('order_total_handshake') }">
        <label>{{ fieldLabel('order_total_handshake') }}</label>
        <input
          type="text"
          class="form-control"
          v-model="formData.order_total_handshake"
          :disabled="disabled"
        >
        <span v-if="showFieldError('order_total_handshake')" class="text-danger help-block">{{ fieldErrorMessage('order_total_handshake') }}</span>
      </div>
    </div>

    <div class="col-md-6">
      <div class="wolt-drive-integration-form__auto-send-row">
        <div class="wolt-drive-integration-form__auto-send-status">
          <div class="form-group" :class="{ 'has-error': showFieldError('auto_send_at_status') }">
            <label>{{ fieldLabel('auto_send_at_status') }}</label>
            <select
              v-model="formData.auto_send_at_status"
              class="form-control"
              :disabled="disabled"
            >
              <option
                v-for="opt in autoSendStatusOptions"
                :key="'wolt-drive-status-' + opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>
            <span v-if="showFieldError('auto_send_at_status')" class="text-danger help-block">{{ fieldErrorMessage('auto_send_at_status') }}</span>
          </div>
        </div>
        <div class="wolt-drive-integration-form__auto-send-time">
          <div class="form-group" :class="{ 'has-error': showFieldError('auto_send_time') }">
            <label>{{ fieldLabel('auto_send_time') }}</label>
            <select
              v-model="formData.auto_send_time"
              class="form-control"
              :disabled="disabled || isAutoSendManual"
            >
              <option
                v-for="mins in autoSendTimeOptions"
                :key="'wolt-drive-time-' + mins"
                :value="mins"
              >
                {{ mins }}
              </option>
            </select>
            <span v-if="showFieldError('auto_send_time')" class="text-danger help-block">{{ fieldErrorMessage('auto_send_time') }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="col-md-6">
      <div class="form-group wolt-drive-integration-form__switch-row">
        <label class="wolt-drive-integration-form__switch-label">&nbsp;</label>
        <div class="wolt-drive-integration-form__switch-control">
          <p-switch
            v-model="formData.active"
            type="success"
            class="mar-bt-0"
            name="wolt-drive-active"
            :disabled="disabled"
          />
          <label for="wolt-drive-active">{{ fieldLabel('active') }}</label>
        </div>
      </div>
    </div>

    <div class="col-md-12">
      <div class="form-group" :class="{ 'has-error': showFieldError('feedback_emails') }">
        <label>{{ fieldLabel('feedback_emails') }}</label>
        <textarea
          class="form-control"
          rows="3"
          v-model="formData.feedback_emails"
          :disabled="disabled"
          :placeholder="$t('gestiune.integrationsFeedbackEmailsHint')"
        />
        <small class="help-block text-muted">{{ $t('gestiune.integrationsFeedbackEmailsHint') }}</small>
        <span v-if="showFieldError('feedback_emails')" class="text-danger help-block">{{ fieldErrorMessage('feedback_emails') }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import PSwitch from 'src/components/UIComponents/Switch.vue'
import {
  getIntegrationFieldErrorMessage,
  hasIntegrationFieldError
} from 'src/components/Dashboard/Views/Integrations/integrationFormFieldHelpers.js'

export const DEFAULT_WOLT_DRIVE_API_URL = 'https://daas-public-api.wolt.com'

export const WOLT_DRIVE_AUTO_SEND_STATUS_VALUES = Object.freeze(['MANUAL', 'PROCESSING', 'PREPARED'])

export const WOLT_DRIVE_AUTO_SEND_TIME_VALUES = Object.freeze(['1', '15', '30', '45', '60'])

export const DEFAULT_WOLT_DRIVE_ORDER_TOTAL_HANDSHAKE = '150'

export default {
  name: 'WoltDriveIntegrationForm',
  components: {
    PSwitch
  },
  props: {
    formData: {
      type: Object,
      required: true
    },
    locations: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    },
    fieldLabelFn: {
      type: Function,
      default: null
    },
    requiredFields: {
      type: Array,
      default: () => []
    },
    validationErrors: {
      type: Object,
      default: () => ({})
    },
    showFieldValidation: {
      type: Boolean,
      default: false
    },
    occupiedLocationIds: {
      type: Array,
      default: () => []
    },
    isCreateMode: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    autoSendStatusOptions () {
      return WOLT_DRIVE_AUTO_SEND_STATUS_VALUES.map(value => ({
        value,
        label: this.autoSendStatusLabel(value)
      }))
    },
    autoSendTimeOptions () {
      return WOLT_DRIVE_AUTO_SEND_TIME_VALUES.slice()
    },
    isAutoSendManual () {
      return String(this.formData.auto_send_at_status || '').toUpperCase() === 'MANUAL'
    }
  },
  created () {
    this.ensureBooleanDefaults()
    this.ensureApiUrlDefault()
    this.ensureAutoSendStatusDefault()
    this.ensureAutoSendTimeDefault()
    this.ensureOrderTotalHandshakeDefault()
  },
  methods: {
    fieldLabel (field) {
      if (typeof this.fieldLabelFn === 'function') return this.fieldLabelFn(field)
      return field
    },
    isRequired (field) {
      return (this.requiredFields || []).includes(field)
    },
    hasFieldError (field) {
      return Boolean(this.validationErrors && this.validationErrors[field])
    },
    showFieldError (field) {
      return hasIntegrationFieldError({
        field,
        validationErrors: this.validationErrors,
        showFieldValidation: this.showFieldValidation,
        requiredFields: this.requiredFields,
        formData: this.formData
      })
    },
    fieldErrorMessage (field) {
      return getIntegrationFieldErrorMessage({
        field,
        validationErrors: this.validationErrors,
        showFieldValidation: this.showFieldValidation,
        requiredFields: this.requiredFields,
        formData: this.formData,
        requiredMessage: this.$t('validations.requiredField')
      })
    },
    isLocationOccupied (locationId) {
      if (locationId == null || locationId === '') return false
      return (this.occupiedLocationIds || []).map(String).includes(String(locationId))
    },
    locationOptionLabel (loc) {
      const name = loc && loc.name ? loc.name : ''
      if (this.isLocationOccupied(loc._integrationLocationId)) {
        return `${name} (${this.$t('gestiune.integrationsLocationOccupied')})`
      }
      return name
    },
    autoSendStatusLabel (value) {
      const key = `gestiune.woltDriveAutoSendStatus_${String(value || '').toLowerCase()}`
      const translated = this.$t(key)
      return translated !== key ? translated : String(value || '')
    },
    ensureBooleanDefaults () {
      if (this.formData.active === undefined || this.formData.active === null || this.formData.active === '') {
        this.$set(this.formData, 'active', false)
      } else {
        this.$set(this.formData, 'active', Boolean(this.formData.active))
      }
    },
    ensureApiUrlDefault () {
      const url = this.formData.api_url
      if (url === undefined || url === null || String(url).trim() === '') {
        this.$set(this.formData, 'api_url', DEFAULT_WOLT_DRIVE_API_URL)
      }
    },
    ensureAutoSendStatusDefault () {
      const raw = this.formData.auto_send_at_status
      let normalized = raw === undefined || raw === null || String(raw).trim() === ''
        ? ''
        : String(raw).trim().toUpperCase()
      if (!normalized || !WOLT_DRIVE_AUTO_SEND_STATUS_VALUES.includes(normalized)) {
        normalized = 'MANUAL'
      }
      this.$set(this.formData, 'auto_send_at_status', normalized)
    },
    ensureAutoSendTimeDefault () {
      const raw = this.formData.auto_send_time
      let normalized = raw === null || raw === undefined || String(raw).trim() === ''
        ? ''
        : String(parseInt(String(raw).trim(), 10))
      if (!normalized || !WOLT_DRIVE_AUTO_SEND_TIME_VALUES.includes(normalized)) {
        normalized = '30'
      }
      this.$set(this.formData, 'auto_send_time', normalized)
    },
    ensureOrderTotalHandshakeDefault () {
      const raw = this.formData.order_total_handshake
      if (raw === undefined || raw === null || String(raw).trim() === '') {
        this.$set(this.formData, 'order_total_handshake', DEFAULT_WOLT_DRIVE_ORDER_TOTAL_HANDSHAKE)
      }
    }
  }
}
</script>

<style scoped>
.wolt-drive-integration-form__auto-send-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
}
.wolt-drive-integration-form__auto-send-status {
  flex: 2 1 0;
  min-width: 0;
}
.wolt-drive-integration-form__auto-send-time {
  flex: 1 0 150px;
  min-width: 150px;
  max-width: 200px;
}
.wolt-drive-integration-form__auto-send-time label {
  white-space: nowrap;
}
.wolt-drive-integration-form__auto-send-time .form-control {
  width: 100%;
}
.wolt-drive-integration-form__switch-row {
  margin-bottom: 0;
}
.wolt-drive-integration-form__switch-label {
  display: block;
  margin-bottom: 0;
  min-height: 1.2em;
}
.wolt-drive-integration-form__switch-control {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 38px;
}
.wolt-drive-integration-form__switch-control label {
  margin: 0;
}
.wolt-drive-integration-form .has-error .form-control {
  border-color: #dc3545;
}
.wolt-drive-integration-form .help-block {
  margin-top: 4px;
  margin-bottom: 0;
}
.wolt-drive-integration-form option.integration-location-option--occupied {
  color: #9e9e9e;
}
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/Integrations/WoltDriveIntegrationForm.vue